import { motion } from 'framer-motion';
import { PageHero, SectionHeader } from '@/components/common/Sections';
import { CTABanner } from '@/components/common/Cards';
import { OFFICES, INTERNATIONAL_OFFICES, IMAGES } from '@/lib/constants';
import { MapPin, Globe, Buildings, Headset } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

export default function GlobalPresencePage() {
  const hq = OFFICES.find(o => o.isHQ);
  const indianHubs = OFFICES.filter(o => !o.isHQ);

  const stats = [
    { icon: Globe, number: "9", label: "International Offices" },
    { icon: Buildings, number: "18", label: "Indian State Hubs" },
    { icon: MapPin, number: "120+", label: "Countries Served" },
    { icon: Headset, number: "24x7", label: "Global Support" },
  ];

  return (
    <div className="page-transition">
      <PageHero
        title="Global Presence"
        subtitle="9 International Offices. 18 Indian Hubs. Serving 120+ countries with local presence and global reach."
        backgroundImage={IMAGES.dubai}
      />

      {/* Stats */}
      <section data-testid="presence-stats" className="bg-[#0A1F3D] border-t border-white/5">
        <div className="container-custom py-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center"
              >
                <stat.icon size={32} className="text-[#D4AF37] mx-auto mb-3" />
                <div className="text-3xl md:text-4xl font-bold font-['Playfair_Display'] text-white mb-1">
                  {stat.number}
                </div>
                <div className="text-white/50 text-sm">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* International Offices */}
      <section data-testid="international-offices" className="section-padding bg-white">
        <div className="container-custom">
          <SectionHeader
            overline="International Network"
            title="9 International Offices"
            description="Strategic locations across 6 continents for seamless service delivery"
          />

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {INTERNATIONAL_OFFICES.map((office, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.06 }}
                data-testid={`intl-office-${office.country.toLowerCase().replace(/\s/g, '-')}`}
                className="group bg-[#F8F8F8] border border-slate-200 p-6 hover:border-[#D4AF37]/40 hover:shadow-lg transition-all duration-300"
              >
                <div className="flex items-center gap-4 mb-4">
                  <span className="text-3xl">{office.flag}</span>
                  <div>
                    <h3 className="text-lg font-bold font-['Playfair_Display'] text-[#0A1F3D]">
                      {office.country}
                    </h3>
                    <p className="text-sm text-[#D4AF37]">{office.city}</p>
                  </div>
                </div>
                <p className="text-xs font-semibold text-[#0A1F3D]/70 uppercase tracking-wider mb-2">
                  {office.company}
                </p>
                <div className="flex items-start gap-2">
                  <MapPin size={14} className="text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-[#4A5568] leading-relaxed">
                    {office.address}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Headquarters */}
      <section data-testid="headquarters" className="bg-[#0A1F3D] relative grain-overlay">
        <div className="container-custom relative z-10 py-16">
          <div className="max-w-2xl mx-auto text-center">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#D4AF37] mb-4 block">
              Corporate Headquarters
            </span>
            <h2 className="text-3xl md:text-4xl font-bold font-['Playfair_Display'] text-white mb-4 tracking-tight">
              {hq?.city}
            </h2>
            <div className="flex items-center justify-center gap-2 text-white/60 mb-2">
              <MapPin size={18} className="text-[#D4AF37]" />
              <span className="text-sm">{hq?.address}</span>
            </div>
            <p className="text-white/40 text-sm">{hq?.phone} &middot; {hq?.email}</p>
          </div>
        </div>
      </section>

      {/* Indian State Hubs */}
      <section data-testid="indian-hubs" className="section-padding bg-[#F5F5F5]">
        <div className="container-custom">
          <SectionHeader
            overline="Pan-India Network"
            title="18 State Hubs Across India"
            description="Presence in metros, Tier 2 and Tier 3 cities for comprehensive coverage"
          />

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {indianHubs.map((hub, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.03 }}
                data-testid={`hub-${hub.city.toLowerCase().replace(/\s/g, '-')}`}
                className="bg-white border border-slate-200 p-5 text-center hover:border-[#D4AF37]/40 hover:shadow-md transition-all duration-300 group"
              >
                <MapPin size={20} className="text-[#D4AF37] mx-auto mb-3 group-hover:scale-110 transition-transform" />
                <h4 className="font-bold text-sm font-['Playfair_Display'] text-[#0A1F3D]">
                  {hub.city}
                </h4>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Support Features */}
      <section data-testid="support-features" className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              { title: "24x7 Support", desc: "Round the clock assistance across all time zones" },
              { title: "Multilingual Team", desc: "Support in major global languages" },
              { title: "Local Numbers", desc: "Direct lines in each region for quick response" },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center p-8 bg-[#F8F8F8] border border-slate-100"
              >
                <h3 className="text-lg font-bold font-['Playfair_Display'] text-[#0A1F3D] mb-2">{item.title}</h3>
                <p className="text-sm text-[#4A5568]">{item.desc}</p>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/contact">
              <Button
                data-testid="contact-nearest-office-btn"
                className="bg-[#D4AF37] text-[#0A1F3D] hover:bg-[#c5a033] px-8 py-6 text-base font-semibold rounded-none"
              >
                Connect With Your Nearest Office
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <CTABanner />
    </div>
  );
}
